<template>
    <div class="signoff-inspection-tab">
        <div v-if="loading && !rows.length" class="text-muted">Loading…</div>
        <div v-else-if="!rows.length" class="text-muted">
            <i class="fa fa-check-circle"></i>
            Nothing waiting for inspection.
        </div>
        <table v-else class="table table-hover">
            <thead>
                <tr>
                    <th style="width: 60px;">#</th>
                    <th>Quote</th>
                    <th>Production done</th>
                    <th>Submitted by</th>
                    <th style="width: 80px; text-align: center;">Approve</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="row in rows">
                    <tr :key="row.id" @click="toggleExpand(row)" style="cursor: pointer;">
                        <td>#{{ row.quote.id }}</td>
                        <td>
                            <strong>{{ row.quote.name }}</strong>
                            <span v-if="row.quote.company && row.quote.company !== row.quote.name" class="text-muted">
                                — {{ row.quote.company }}
                            </span>
                        </td>
                        <td>
                            <small>{{ formatRelative(row.submitted_at) }}</small>
                        </td>
                        <td>
                            <small v-if="row.submitter">{{ row.submitter.name }}</small>
                            <small v-else class="text-muted">auto (all ticked)</small>
                        </td>
                        <td style="text-align: center;" @click.stop>
                            <input
                                type="checkbox"
                                :disabled="approving[row.id]"
                                @change="onApprove(row)"
                                title="Tick to approve — inspection label prints automatically"
                            />
                            <button
                                v-if="canOverride"
                                type="button"
                                class="btn btn-default btn-xs"
                                style="margin-left: 6px;"
                                :disabled="approving[row.id]"
                                @click.stop="onOverride(row)"
                                title="Override — sign off on behalf of the queued inspector with a reason"
                            ><i class="fa fa-shield"></i> Override</button>
                        </td>
                    </tr>
                    <tr v-if="expanded === row.id" :key="row.id + '-detail'">
                        <td colspan="5" style="background: #f8f9fa; padding: 14px 24px;">
                            <div v-if="loadingDetail">Loading detail…</div>
                            <div v-else-if="detail && detail.id === row.quote.id">
                                <div class="row" style="margin-bottom: 12px;">
                                    <div class="col-sm-6">
                                        <h5 style="margin-top: 0;">Total labour</h5>
                                        <div style="font-size: 1.4em;"><strong>{{ detail.total_labour_hours }}h</strong></div>
                                    </div>
                                    <div class="col-sm-6 text-right">
                                        <button
                                            type="button"
                                            class="btn btn-default btn-sm"
                                            :disabled="reprinting"
                                            @click.stop="onReprint(row)"
                                            v-if="detail.signoff && detail.signoff.signed_off_at"
                                            title="Reprint the inspection sticker if the printer was offline">
                                            <i class="fa fa-print"></i> Reprint inspection sticker
                                        </button>
                                    </div>
                                </div>

                                <h5>Processes</h5>
                                <table class="table table-condensed table-bordered" style="background: white;">
                                    <thead>
                                        <tr>
                                            <th>Process</th>
                                            <th>Checked by</th>
                                            <th style="width: 100px;">Duration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="p in detail.processes" :key="p.id">
                                            <td>
                                                <i v-if="p.checked" class="fa fa-check-square-o text-success"></i>
                                                <i v-else class="fa fa-square-o text-muted"></i>
                                                {{ p.process_name }}
                                            </td>
                                            <td>
                                                <span v-if="p.checkers.length === 0" class="text-muted">—</span>
                                                <span v-for="(c, i) in p.checkers" :key="i">
                                                    {{ c.user_name }}<span v-if="c.checked_at"> ({{ formatTime(c.checked_at) }})</span><span v-if="i < p.checkers.length - 1">, </span>
                                                </span>
                                            </td>
                                            <td>
                                                <span v-if="p.duration_minutes !== null">{{ p.duration_minutes }} min</span>
                                                <span v-else class="text-muted">—</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <h5 style="margin-top: 14px;">Items &amp; materials</h5>
                                <ul class="list-unstyled">
                                    <li v-for="item in detail.items" :key="item.id" style="margin-bottom: 10px;">
                                        <strong>{{ item.name }}</strong>
                                        <span class="text-muted">(qty {{ item.quantity }})</span>
                                        <ul style="margin-top: 4px;">
                                            <template v-for="(value, key) in item.materials">
                                                <li v-if="key === 'extras' && Array.isArray(value)" :key="item.id + '-extras'">
                                                    <strong>Extras:</strong>
                                                    <ul>
                                                        <li v-for="(extra, ei) in value" :key="ei">
                                                            {{ extra.item }}
                                                            <span v-if="extra.price" class="text-muted">(£{{ (extra.price / 100).toFixed(2) }})</span>
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li v-else-if="key !== 'extras'" :key="item.id + '-' + key">
                                                    <span class="text-muted">{{ key }}:</span> {{ value }}
                                                </li>
                                            </template>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
/*
    Inspection tab — Frances/Simon (Peter has visibility) approves
    finished production. Tick = approve + auto-print label. Detail
    shows processes (checker + duration), total labour, materials.

    The approve POST returns within ~3-4s even if the printer is
    offline (printInspection has a 3s timeout and never throws). On
    print failure the row is removed from the list but a Reprint
    button stays available on the row's expanded detail.

    See planning/quote-signoff-design.md §6, §7.4, §7.7.
*/
export default {
    props: {
        canOverride: { type: Boolean, default: false },
    },
    data() {
        return {
            rows: [],
            loading: true,
            expanded: null,
            detail: null,
            loadingDetail: false,
            approving: {},
            reprinting: false,
        };
    },
    mounted() { this.refresh(); },
    methods: {
        refresh() {
            this.loading = true;
            fetch('/api/signoff/inspection/list', { headers: { Accept: 'application/json' }})
                .then(r => r.json())
                .then(d => { this.rows = d.data || d; this.loading = false; })
                .catch(() => { this.loading = false; });
        },
        toggleExpand(row) {
            if (this.expanded === row.id) {
                this.expanded = null;
                this.detail = null;
                return;
            }
            this.expanded = row.id;
            this.loadingDetail = true;
            this.detail = null;
            fetch(`/api/signoff/${row.quote.id}/inspection-detail`, { headers: { Accept: 'application/json' }})
                .then(r => r.json())
                .then(d => { this.detail = d; this.loadingDetail = false; })
                .catch(() => { this.loadingDetail = false; });
        },
        onApprove(row) {
            if (this.approving[row.id]) return;
            this.$set(this.approving, row.id, true);

            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/quote/${row.quote.id}/signoff/inspection/approve`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
            })
                .then(r => r.json().then(b => ({ status: r.status, body: b })))
                .then(({ status, body }) => {
                    if (status >= 400) {
                        alert(body.error || 'Approve failed');
                        this.$set(this.approving, row.id, false);
                        return;
                    }
                    if (body.print && body.print.ok === false) {
                        alert('Approved ✓ — but inspection label print FAILED. Open the row to reprint when the printer is back.');
                    }
                    this.rows = this.rows.filter(r => r.id !== row.id);
                    if (this.expanded === row.id) this.expanded = null;
                    this.$emit('approved', { quoteId: row.quote.id, stage: 'inspection' });
                })
                .catch(err => {
                    alert('Approve failed: ' + (err.message || 'network error'));
                    this.$set(this.approving, row.id, false);
                });
        },
        onOverride(row) {
            if (this.approving[row.id]) return;
            const reason = window.prompt(
                'OVERRIDE inspection signoff for #' + row.quote.id + ' — sign off on behalf of the inspector.\n\nReason (min 10 chars):',
                ''
            );
            if (reason === null || reason.trim().length < 10) {
                if (reason !== null) alert('Override cancelled — reason must be at least 10 characters.');
                return;
            }
            this.$set(this.approving, row.id, true);

            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/quote/${row.quote.id}/signoff/inspection/override`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
                body: JSON.stringify({ override_reason: reason.trim() }),
            })
                .then(r => r.json().then(b => ({ status: r.status, body: b })))
                .then(({ status, body }) => {
                    if (status >= 400) {
                        alert((body && (body.message || body.error)) || 'Override failed');
                        this.$set(this.approving, row.id, false);
                        return;
                    }
                    if (body.print && body.print.ok === false) {
                        alert('Overridden ✓ — but inspection label print FAILED. Open the row to reprint when the printer is back.');
                    }
                    this.rows = this.rows.filter(r => r.id !== row.id);
                    if (this.expanded === row.id) this.expanded = null;
                    this.$emit('approved', { quoteId: row.quote.id, stage: 'inspection', override: true });
                })
                .catch(err => {
                    alert('Override failed: ' + (err.message || 'network error'));
                    this.$set(this.approving, row.id, false);
                });
        },
        onReprint(row) {
            if (this.reprinting) return;
            this.reprinting = true;

            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/quote/${row.quote.id}/inspection/reprint-label`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
            })
                .then(r => r.json())
                .then(body => {
                    this.reprinting = false;
                    if (body.ok) {
                        alert('Reprint sent ✓');
                    } else {
                        alert('Reprint FAILED: ' + (body.print?.reason || 'unknown error'));
                    }
                })
                .catch(err => {
                    this.reprinting = false;
                    alert('Reprint failed: ' + (err.message || 'network error'));
                });
        },
        formatRelative(iso) {
            if (!iso) return '';
            const then = new Date(iso).getTime();
            const diffMins = Math.floor((Date.now() - then) / 60000);
            if (diffMins < 60) return `${diffMins}m ago`;
            const hours = Math.floor(diffMins / 60);
            if (hours < 24) return `${hours}h ago`;
            return `${Math.floor(hours / 24)}d ago`;
        },
        formatTime(iso) {
            if (!iso) return '';
            const d = new Date(iso);
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        },
    },
};
</script>
